import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';

export interface ConfirmModalProps {
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Destructive styling (delete / ban style actions). */
    danger?: boolean;
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Shop-styled confirmation dialog (replaces native window.confirm).
 * Uses SiteSettings CSS vars so it matches the rest of the marketplace.
 */
const ConfirmModal = ({
    open,
    title,
    message,
    confirmLabel,
    cancelLabel,
    danger = false,
    busy = false,
    onConfirm,
    onCancel,
}: ConfirmModalProps) => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[10050]" onClose={() => !busy && onCancel()}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[var(--bg-color)] p-6 shadow-xl">
                                <Dialog.Title className="text-lg font-semibold text-[var(--text-color)]">
                                    {title}
                                </Dialog.Title>
                                <p className="mt-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_25%)]">
                                    {message}
                                </p>
                                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={onCancel}
                                        className="min-h-11 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] px-4 py-2.5 text-sm font-medium text-[var(--text-color)] transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] disabled:opacity-60"
                                    >
                                        {cancelLabel || tr('common.cancel', 'Cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={onConfirm}
                                        className={`min-h-11 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition disabled:opacity-60 ${
                                            danger
                                                ? 'bg-red-600 hover:bg-red-700'
                                                : 'bg-[var(--accent-color)] hover:opacity-90'
                                        }`}
                                    >
                                        {busy
                                            ? tr('state.loading', 'Loading…')
                                            : confirmLabel || tr('common.confirm', 'Confirm')}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default ConfirmModal;
